import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { AuthSession, Role } from "./types";
import { AppShell, PublicShell } from "./components/AppShell";
import { Login } from "./components/Login";
const HomePage = lazy(() => import("./pages/HomePage").then((m) => ({ default: m.HomePage })));
const StudentPage = lazy(() => import("./pages/StudentPage").then((m) => ({ default: m.StudentPage })));
const DocsPage = lazy(() => import("./pages/DocsPage").then((m) => ({ default: m.DocsPage })));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage").then((m) => ({ default: m.ResourcesPage })));
const MonitorPage = lazy(() => import("./pages/MonitorPage").then((m) => ({ default: m.MonitorPage })));
const TopologyPage = lazy(() => import("./pages/TopologyPage").then((m) => ({ default: m.TopologyPage })));
const ToolsPage = lazy(() => import("./pages/ToolsPage").then((m) => ({ default: m.ToolsPage })));
const InstructorPage = lazy(() => import("./pages/InstructorPage").then((m) => ({ default: m.InstructorPage })));

function allowed(role: Role | undefined, roles: Role[]) { return !!role && roles.includes(role); }

export function App() {
  const client = useQueryClient();
  const location = useLocation();
  const session = useQuery({ queryKey: ["me"], queryFn: () => api<AuthSession>("/api/me") });
  const [authVersion, setAuthVersion] = useState(0);
  useEffect(() => { if (authVersion) client.invalidateQueries({ queryKey: ["me"] }); }, [authVersion, client]);
  if (session.isLoading) return <div className="center-state">กำลังเปิด Workshop…</div>;
  if (!session.data?.authenticated) {
    if (location.pathname === "/resources" || location.pathname === "/resources/") {
      return <Suspense fallback={<div className="center-state">กำลังเปิดหน้า…</div>}>
        <PublicShell><ResourcesPage /></PublicShell>
      </Suspense>;
    }
    if (location.pathname === "/docs" || location.pathname === "/docs/") {
      return <Suspense fallback={<div className="center-state">กำลังเปิดหน้า…</div>}>
        <PublicShell><DocsPage /></PublicShell>
      </Suspense>;
    }
    return <Login onAuthenticated={() => setAuthVersion((v) => v + 1)} />;
  }
  const role = session.data.role;
  const page = (element: React.ReactNode, roles: Role[]) => allowed(role, roles) ? <AppShell session={session.data!}>{element}</AppShell> : <Navigate to={role === "instructor" ? "/instructor" : role === "monitor" ? "/monitor" : "/home"} replace />;
  return <Suspense fallback={<div className="center-state">กำลังเปิดหน้า…</div>}><Routes>
    {/* "/" is the single login entry point; authenticated users use /home. */}
    <Route path="/" element={<Navigate to="/home" replace />} />
    <Route path="/login" element={<Navigate to="/home" replace />} />
    <Route path="/home" element={page(<HomePage session={session.data} />, ["participant", "instructor"])} />
    <Route path="/lab" element={page(<StudentPage session={session.data} />, ["participant", "instructor"])} />
    <Route path="/docs" element={page(<DocsPage session={session.data} />, ["participant", "instructor"])} />
    <Route path="/resources" element={<PublicShell signedIn><ResourcesPage /></PublicShell>} />
    <Route path="/monitor" element={page(<MonitorPage />, ["participant", "instructor", "monitor"])} />
    <Route path="/topology" element={page(<TopologyPage session={session.data} />, ["participant", "instructor", "monitor"])} />
    <Route path="/tools" element={page(<ToolsPage session={session.data} />, ["participant", "instructor", "monitor"])} />
    <Route path="/instructor/*" element={page(<InstructorPage />, ["instructor"])} />
    <Route path="*" element={<Navigate to="/home" replace />} />
  </Routes></Suspense>;
}
